import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, useTenantBootstrap, tenantKeys } from '@tark/shared/hooks';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Input } from '@tark/shared/components';
import { acceptTenantInvitation, redeemTenantJoinCode } from '@tark/shared/lib';
import { Building2, KeyRound, Ticket } from 'lucide-react';

export default function JoinOrganizationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { memberships, activeTenant } = useTenantBootstrap(Boolean(user));
  const [joinCode, setJoinCode] = useState('');
  const [invitationToken, setInvitationToken] = useState('');
  const [isRedeemingCode, setIsRedeemingCode] = useState(false);
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const token = searchParams.get('token');
    if (code) {
      setJoinCode(code);
    }
    if (token) {
      setInvitationToken(token);
    }
  }, [searchParams]);

  const handleJoinCodeRedeem = async () => {
    const code = joinCode.trim();
    if (!code) {
      setError('Enter the organization join code your coach shared.');
      return;
    }

    setIsRedeemingCode(true);
    setError(null);
    setMessage(null);
    try {
      const bootstrap = await redeemTenantJoinCode({ code });
      queryClient.setQueryData(tenantKeys.bootstrap(), bootstrap);
      setMessage(`Joined ${bootstrap.activeTenant?.tenant?.displayName || 'your organization'}.`);
      setJoinCode('');
      navigate('/');
    } catch (joinError: any) {
      setError(joinError?.response?.data?.message || joinError?.message || 'Could not join the organization.');
    } finally {
      setIsRedeemingCode(false);
    }
  };

  const handleInvitationAccept = async () => {
    const token = invitationToken.trim();
    if (!token) {
      setError('Paste the invitation token or full invite token your coach sent.');
      return;
    }

    setIsAcceptingInvitation(true);
    setError(null);
    setMessage(null);
    try {
      const bootstrap = await acceptTenantInvitation({ invitationToken: token });
      queryClient.setQueryData(tenantKeys.bootstrap(), bootstrap);
      setMessage(`Accepted access to ${bootstrap.activeTenant?.tenant?.displayName || 'your organization'}.`);
      setInvitationToken('');
      navigate('/');
    } catch (acceptError: any) {
      setError(acceptError?.response?.data?.message || acceptError?.message || 'Could not accept the invitation.');
    } finally {
      setIsAcceptingInvitation(false);
    }
  };

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Join Organization
          </CardTitle>
          <CardDescription>
            Keep your same TARK account. Add organization membership when a coach shares a guardian join code or a direct invitation.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-xl border border-border bg-surface-elevated/50 p-4">
            <p className="text-sm font-medium text-text">Current Context</p>
            <p className="mt-2 text-base font-semibold text-white">
              {activeTenant?.tenant?.displayName || 'Community'}
            </p>
            <p className="mt-1 text-sm text-text-muted">
              {memberships.length > 0
                ? `You already belong to ${memberships.length} organization${memberships.length === 1 ? '' : 's'}.`
                : 'You are currently using TARK in community mode.'}
            </p>
          </div>

          {message ? (
            <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-200">
              {message}
            </div>
          ) : null}

          {error ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card variant="default">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4 text-primary" />
              Join With Code
            </CardTitle>
            <CardDescription>
              Best for parent/guardian access when a coach shares a reusable organization code.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Organization Code"
              value={joinCode}
              onChange={(event) => setJoinCode(event.target.value)}
              placeholder="Example: PAMO2026"
              autoCapitalize="characters"
            />
            <Button onClick={handleJoinCodeRedeem} isLoading={isRedeemingCode}>
              Join Organization
            </Button>
          </CardContent>
        </Card>

        <Card variant="default">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Ticket className="h-4 w-4 text-primary" />
              Accept Invitation
            </CardTitle>
            <CardDescription>
              Use this when your coach or academy sent you a direct invite token for a specific role.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Invitation Token"
              value={invitationToken}
              onChange={(event) => setInvitationToken(event.target.value)}
              placeholder="Paste your invite token"
            />
            <Button variant="secondary" onClick={handleInvitationAccept} isLoading={isAcceptingInvitation}>
              Accept Invitation
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
